/**
 * Research History Tab Component
 *
 * Displays research job history for an enterprise system with:
 * - List of research jobs (most recent first)
 * - Real-time progress updates for running jobs
 * - 8-phase progress breakdown
 * - Results summary
 * - Trigger new research button
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Play,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
  AlertCircle,
  Database,
  Code,
  Layers,
  FileText,
  Network,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getResearchJobs,
  triggerSystemResearch,
  cancelResearchJob,
  type ResearchJob,
  type ResearchPhaseProgress,
} from '../../lib/api/enterpriseSystemsApi';

interface ResearchHistoryTabProps {
  systemId: string;
  systemName: string;
}

const ResearchHistoryTab: React.FC<ResearchHistoryTabProps> = ({
  systemId,
  systemName,
}) => {
  const queryClient = useQueryClient();
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  // Fetch research jobs
  const {
    data: jobs = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['research-jobs', systemId],
    queryFn: () => getResearchJobs(systemId),
    staleTime: 0, // Always consider data stale to ensure fresh fetches
    refetchInterval: (query) => {
      // Auto-refresh every 5 seconds if any job is running
      // In React Query v5, the callback receives query object with state.data
      const data = query.state.data;
      if (!data || !Array.isArray(data)) return false;
      const hasRunningJob = data.some((job) => job.status === 'running' || job.status === 'pending');
      return hasRunningJob ? 5000 : false;
    },
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Trigger new research mutation
  const triggerResearchMutation = useMutation({
    mutationFn: () => triggerSystemResearch(systemId, false),
    onSuccess: (data) => {
      toast.success(`Research started! Job ID: ${data.job_id}`);
      queryClient.invalidateQueries({ queryKey: ['research-jobs', systemId] });
    },
    onError: (error: any) => {
      toast.error(`Failed to start research: ${error.message}`);
    },
  });

  // Cancel job mutation
  const cancelJobMutation = useMutation({
    mutationFn: (jobId: string) => cancelResearchJob(jobId),
    onSuccess: () => {
      toast.success('Research job cancelled');
      queryClient.invalidateQueries({ queryKey: ['research-jobs', systemId] });
    },
    onError: (error: any) => {
      toast.error(`Failed to cancel job: ${error.message}`);
    },
  });

  /**
   * Get status color and icon
   */
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          label: 'Completed',
        };
      case 'running':
        return {
          icon: Loader2,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          label: 'Running',
          animate: true,
        };
      case 'pending':
        return {
          icon: Clock,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          label: 'Pending',
        };
      case 'failed':
        return {
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          label: 'Failed',
        };
      default:
        return {
          icon: AlertCircle,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          label: status,
        };
    }
  };

  /**
   * Format duration
   */
  const formatDuration = (startedAt: string, completedAt?: string) => {
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const durationMs = end.getTime() - start.getTime();
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  /**
   * Format phase name for display
   */
  const formatPhaseName = (phase: string | null | undefined): string => {
    if (!phase) return 'Unknown Phase';
    return phase
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
        <span className="ml-3 text-neutral-600">Loading research history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Research History</h3>
            <p className="text-sm text-red-700 mt-1">{(error as Error).message}</p>
            <button
              onClick={() => refetch()}
              className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Trigger Button */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">Research History</h2>
          <p className="text-sm text-neutral-600 mt-1">
            AI-powered research jobs for discovering system intelligence
          </p>
        </div>
        <button
          onClick={() => triggerResearchMutation.mutate()}
          disabled={triggerResearchMutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {triggerResearchMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Starting...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Trigger New Research
            </>
          )}
        </button>
      </div>

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-12 text-center">
          <Database className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 mb-2">No Research Jobs Yet</h3>
          <p className="text-neutral-600 mb-6">
            Start your first research job to discover modules, schema, APIs, and documentation
          </p>
          <button
            onClick={() => triggerResearchMutation.mutate()}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Play className="w-5 h-5" />
            Start Research
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => {
            const statusDisplay = getStatusDisplay(job.status);
            const StatusIcon = statusDisplay.icon;
            const isExpanded = expandedJobId === job.job_id;

            return (
              <div
                key={job.job_id}
                className={`bg-white border ${statusDisplay.borderColor} rounded-lg overflow-hidden`}
              >
                {/* Job Header */}
                <div
                  className="p-6 cursor-pointer hover:bg-neutral-50 transition-colors"
                  onClick={() => setExpandedJobId(isExpanded ? null : job.job_id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Status Icon */}
                      <div className={`p-3 ${statusDisplay.bgColor} rounded-lg`}>
                        <StatusIcon
                          className={`w-6 h-6 ${statusDisplay.color} ${
                            statusDisplay.animate ? 'animate-spin' : ''
                          }`}
                        />
                      </div>

                      {/* Job Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-neutral-900">
                            {job.system_name}
                          </h3>
                          <span
                            className={`px-2.5 py-1 ${statusDisplay.bgColor} ${statusDisplay.color} text-xs font-medium rounded-full`}
                          >
                            {statusDisplay.label}
                          </span>
                        </div>

                        <div className="flex items-center gap-6 text-sm text-neutral-600">
                          <span>Job ID: {job.job_id}</span>
                          {job.started_at ? (
                            <span>Started: {new Date(job.started_at).toLocaleString()}</span>
                          ) : (
                            <span>Status: Waiting to start...</span>
                          )}
                          {job.completed_at && job.started_at && (
                            <span>Duration: {formatDuration(job.started_at, job.completed_at)}</span>
                          )}
                        </div>

                        {/* Progress Bar */}
                        {(job.status === 'running' || job.status === 'pending') && (
                          <div className="mt-4">
                            <div className="flex items-center justify-between text-sm mb-2">
                              <span className="text-neutral-700 font-medium">
                                {formatPhaseName(job.current_phase)}
                              </span>
                              <span className="text-neutral-600">{job.overall_progress ?? 0}%</span>
                            </div>
                            <div className="w-full bg-neutral-200 rounded-full h-2">
                              <div
                                className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${job.overall_progress ?? 0}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Results Summary */}
                        {job.status === 'completed' && job.results && (
                          <div className="mt-4 flex items-center gap-6 text-sm">
                            <div className="flex items-center gap-2 text-neutral-700">
                              <Layers className="w-4 h-4 text-primary-600" />
                              <span>{job.results.modules_found} modules</span>
                            </div>
                            <div className="flex items-center gap-2 text-neutral-700">
                              <Database className="w-4 h-4 text-primary-600" />
                              <span>{job.results.tables_found} tables</span>
                            </div>
                            <div className="flex items-center gap-2 text-neutral-700">
                              <Code className="w-4 h-4 text-primary-600" />
                              <span>{job.results.apis_found} APIs</span>
                            </div>
                            <div className="flex items-center gap-2 text-neutral-700">
                              <FileText className="w-4 h-4 text-primary-600" />
                              <span>{job.results.documentation_links_found} docs</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      {job.status === 'running' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Cancel this research job?')) {
                              cancelJobMutation.mutate(job.job_id);
                            }
                          }}
                          className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                      <button className="text-neutral-400 hover:text-neutral-600">
                        {isExpanded ? '▲' : '▼'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Phase Details */}
                {isExpanded && (
                  <div className="border-t border-neutral-200 p-6 bg-neutral-50">
                    <h4 className="text-sm font-semibold text-neutral-900 mb-4">
                      Phase Progress (8 Phases)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {job.phase_progress?.map((phase, index) => {
                        const phaseStatus = getStatusDisplay(phase.status);
                        const PhaseIcon = phaseStatus.icon;

                        return (
                          <div
                            key={phase.phase}
                            className={`p-4 bg-white border ${phaseStatus.borderColor} rounded-lg`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <PhaseIcon
                                  className={`w-4 h-4 ${phaseStatus.color} ${
                                    phaseStatus.animate ? 'animate-spin' : ''
                                  }`}
                                />
                                <span className="text-sm font-medium text-neutral-900">
                                  {index + 1}. {formatPhaseName(phase.phase)}
                                </span>
                              </div>
                              <span className="text-xs text-neutral-600">
                                {phase.progress_percentage}%
                              </span>
                            </div>
                            <div className="w-full bg-neutral-200 rounded-full h-1.5 mb-2">
                              <div
                                className={`h-1.5 rounded-full ${
                                  phase.status === 'completed'
                                    ? 'bg-green-500'
                                    : phase.status === 'running'
                                    ? 'bg-blue-500'
                                    : phase.status === 'failed'
                                    ? 'bg-red-500'
                                    : 'bg-neutral-300'
                                }`}
                                style={{ width: `${phase.progress_percentage}%` }}
                              />
                            </div>
                            <div className="text-xs text-neutral-600">
                              {phase.items_found > 0 && `${phase.items_found} items found`}
                              {phase.error_message && (
                                <span className="text-red-600">{phase.error_message}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Error Message */}
                    {job.error_message && (
                      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h5 className="text-sm font-medium text-red-800">Error Details</h5>
                            <p className="text-sm text-red-700 mt-1">{job.error_message}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ResearchHistoryTab;
